import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';

const stats = [
  { value: '< 60s', label: 'Response Time', detail: 'Every lead gets an intelligent reply in under 60 seconds.', color: '#FF2B2B' },
  { value: '3x', label: 'Conversion Rate', detail: 'Speed-to-lead is the #1 driver of conversion.', color: '#3FA9F5' },
  { value: '+40%', label: 'Booked Appointments', detail: 'Frictionless automated booking fills your calendar.', color: '#FF2B2B' },
  { value: '80%', label: 'Less Manual Work', detail: 'Your team focuses on delivery, not chasing leads.', color: '#3FA9F5' },
];

export default function ResultsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="results" ref={ref} className={`relative py-32 md:py-44 overflow-hidden ${isDark ? 'bg-[#050505]' : 'bg-[#F5F5F7]'}`}>
      <div className={`absolute top-0 left-0 right-0 h-px ${isDark ? 'bg-[#1A1A1A]' : 'bg-[#E8E8ED]'}`}></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <div
          className="text-center mb-24"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}
        >
          <span className="text-[#FF2B2B] text-xs font-bold tracking-widest uppercase block mb-6">Results</span>
          <h2
            className={`text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-6 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
            style={{ letterSpacing: '-0.03em' }}
          >
            What This Means<br />for <span className="text-[#FF2B2B]">Your Business.</span>
          </h2>
          <p className={`text-lg md:text-xl max-w-xl mx-auto ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`} style={{ fontWeight: 300 }}>
            You don&apos;t need more leads. <strong className={isDark ? 'text-white' : 'text-[#1D1D1F]'}>You need a smarter system.</strong>
          </p>
        </div>

        {/* Giant stats — Apple style 4-across */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`p-8 rounded-3xl border text-center ${isDark ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-[#E8E8ED]'}`}
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s` }}
            >
              <div
                className="font-black mb-3 leading-none"
                style={{ fontSize: 'clamp(40px, 6vw, 72px)', letterSpacing: '-0.04em', color: s.color }}
              >
                {s.value}
              </div>
              <div className={`text-xs font-bold tracking-widest uppercase mb-3 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>{s.label}</div>
              <p className={`text-xs leading-relaxed ${isDark ? 'text-[#C9CED6]/50' : 'text-[#6E6E73]'}`}>{s.detail}</p>
            </div>
          ))}
        </div>

        {/* Full-width statement card */}
        <div
          className={`relative rounded-3xl overflow-hidden ${isDark ? 'bg-[#0D0D0D] border border-[#1A1A1A]' : 'bg-white border border-[#E8E8ED]'}`}
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.4s' }}
        >
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="p-10 md:p-14 flex flex-col justify-center">
              <div className="text-[#FF2B2B] text-xs font-bold tracking-widest uppercase mb-5">The Bottom Line</div>
              <h3
                className={`text-2xl md:text-4xl font-black leading-tight mb-5 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
                style={{ letterSpacing: '-0.03em' }}
              >
                Stop leaking revenue.<br />
                Start converting<br />
                <span className="text-[#FF2B2B]">what you already have.</span>
              </h3>
              <p className={`text-base leading-relaxed ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`} style={{ fontWeight: 300 }}>
                A Bot Smarter gives you the infrastructure to capture every lead,
                qualify automatically, and book appointments without lifting a finger.
              </p>
            </div>
            <div className="relative" style={{ minHeight: 300 }}>
              <img
                src="https://public.readdy.ai/ai/img_res/0879eaa727cbef4316b62abac8442baf.jpg"
                alt="Business results"
                className="w-full h-full object-cover object-top"
              />
              <div className={`absolute inset-0 bg-gradient-to-r ${isDark ? 'from-[#0D0D0D] via-transparent' : 'from-white via-transparent'} to-transparent`}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
